'use client';

import { XIcon } from 'lucide-react';
import {
  Root,
  Title,
  Close,
  Portal,
  Content,
  Overlay,
  Trigger,
  Description,
} from '@radix-ui/react-dialog';

import type { ComponentProps, JSX } from 'react';

import { cn } from '~/utils';

export function Dialog(props: ComponentProps<typeof Root>): JSX.Element {
  return <Root data-slot='dialog' {...props} />;
}

export function DialogTrigger(
  props: ComponentProps<typeof Trigger>,
): JSX.Element {
  return <Trigger data-slot='dialog-trigger' {...props} />;
}

export function DialogPortal(
  props: ComponentProps<typeof Portal>,
): JSX.Element {
  return <Portal data-slot='dialog-portal' {...props} />;
}

export function DialogClose(props: ComponentProps<typeof Close>): JSX.Element {
  return <Close data-slot='dialog-close' {...props} />;
}

export function DialogOverlay({
  className,
  ...props
}: ComponentProps<typeof Overlay>): JSX.Element {
  return (
    <Overlay
      data-slot='dialog-overlay'
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[3000] bg-black/50',
        className,
      )}
      {...props}
    />
  );
}

export function DialogContent({
  children,
  className,
  ...props
}: ComponentProps<typeof Content>): JSX.Element {
  return (
    <DialogPortal data-slot='dialog-portal'>
      <DialogOverlay />
      <Content
        data-slot='dialog-content'
        className={cn(
          'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-[3000] grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg',
          className,
        )}
        {...props}
      >
        {children}
        <Close className="ring-offset-background focus:ring-ring cursor-pointer data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
          <XIcon />
          <span className='sr-only'>Close</span>
        </Close>
      </Content>
    </DialogPortal>
  );
}

export function DialogHeader({
  className,
  ...props
}: ComponentProps<'div'>): JSX.Element {
  return (
    <div
      data-slot='dialog-header'
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  );
}

export function DialogFooter({
  className,
  ...props
}: ComponentProps<'div'>): JSX.Element {
  return (
    <div
      data-slot='dialog-footer'
      className={cn(
        'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    />
  );
}

export function DialogTitle({
  className,
  ...props
}: ComponentProps<typeof Title>): JSX.Element {
  return (
    <Title
      data-slot='dialog-title'
      className={cn('text-lg leading-none font-semibold', className)}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof Description>): JSX.Element {
  return (
    <Description
      data-slot='dialog-description'
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}
